<template>
  <div class="quiz-manager">
    <!-- Loading Overlay -->
    <b-loading :is-full-page="false" :active="isProcessing"></b-loading>
    <ScenarioPuzzle
      ref="puzzleComponent"
      v-if="currentResult"
      :pgn="currentResult.pgn"
      :answerPgn="currentResult.answerPgn"
      :answerStatus="currentResult.status"
      :maxFailedAttempts="isReviewMode ? 2 : 1"
      :quizMode="!isReviewMode"
      :is-presentation-mode="isPresentationMode"
      @puzzle-complete="handlePuzzleComplete"
      @puzzle-failed="handlePuzzleFailed"
    />

    <div
      class="quiz-controls"
      :class="{ 'presentation-mode': isPresentationMode }"
    >
      <QuizNavigation
        :current-index="currentIndex"
        :total-items="scenarios.length"
        :results="puzzleResults"
        :can-navigate-to-past="allowNavigation"
        @navigate="navigateToPuzzle"
      />

      <div class="quiz-buttons">
        <div>
          <b-button
            label="Exit Quiz"
            type="is-danger is-light"
            outlined
            @click="exitQuiz"
          />
          <b-button
            v-if="isReviewMode"
            class="ml-2"
            label="Try Again"
            type="is-primary is-light"
            outlined
            @click="restartQuiz"
          />
        </div>

        <span class="">
          Score: {{ correctCount }} / {{ attemptedPuzzleCount }}
        </span>

        <div>
          <b-button
            v-if="isReviewMode"
            label="Show Results"
            type="is-success is-light"
            outlined
            @click="showResultsModal"
          />

          <b-button
            v-else
            label="Skip Puzzle"
            type="is-warning is-light"
            outlined
            :disabled="isProcessing"
            @click="skipPuzzle"
          />
        </div>
      </div>
    </div>

    <b-modal
      v-model="isResultsModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
      :width="1000"
      @keydown.stop
    >
      <QuizResult
        :is-active="isResultsModalActive"
        :results="puzzleResults"
        :correct-count="correctCount"
        :total-scenarios="scenarios.length"
        @close="isResultsModalActive = false"
        @review="navigateToPuzzle"
      />
    </b-modal>
  </div>
</template>

<script>
import ScenarioPuzzle from "../scenarios/ScenarioPuzzle.vue";
import QuizNavigation from "./QuizNavigation.vue";
import QuizResult from "./QuizResult.vue";

export default {
  name: "QuizSession",

  components: {
    ScenarioPuzzle,
    QuizNavigation,
    QuizResult,
  },

  props: {
    scenarios: {
      type: Array,
      required: true,
      validator(value) {
        return value.every(
          (scenario) =>
            typeof scenario === "object" &&
            typeof scenario.pgn === "string" &&
            (!scenario.initialFen || typeof scenario.initialFen === "string")
        );
      },
    },

    isPresentationMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentIndex: 0,
      isResultsModalActive: false,
      /** @type {import('vue-chess-shared/types/quiz').PuzzleResult[]} */
      puzzleResults: [],
      isReviewMode: false,
      isProcessing: false,
      /** @type {number | null} */
      nextPuzzleTimeoutId: null,
    };
  },

  computed: {
    currentResult() {
      if (this.currentIndex < 0 || this.currentIndex >= this.scenarios.length) {
        return null;
      }
      return this.puzzleResults[this.currentIndex] || null;
    },

    allowNavigation() {
      return this.isReviewMode;
    },

    correctCount() {
      return (
        this.puzzleResults?.filter((result) => result.status === "correct")
          .length || 0
      );
    },

    attemptedPuzzleCount() {
      return (
        this.puzzleResults?.filter((result) => result.status !== null).length ||
        0
      );
    },
  },

  watch: {
    scenarios: {
      immediate: true,
      handler() {
        this.resetPuzzleResults();
      },
    },
  },

  methods: {
    savePuzzleResult({ status, answerPgn }) {
      if (this.isReviewMode) return;

      if (!this.currentResult) {
        console.error(
          "savePuzzleResult: no result found for scenario #",
          this.currentIndex
        );
        return;
      }

      this.currentResult.status = status;
      this.currentResult.answerPgn = answerPgn;
    },

    handlePuzzleComplete(answerPgn) {
      if (this.isProcessing) return;
      this.isProcessing = true;
      this.savePuzzleResult({
        status: "correct",
        answerPgn,
      });
      this.checkQuizComplete();
    },

    handlePuzzleFailed(answerPgn) {
      if (this.isProcessing) return;
      this.isProcessing = true;
      this.savePuzzleResult({
        status: "incorrect",
        answerPgn,
      });
      this.checkQuizComplete();
    },

    skipPuzzle() {
      if (this.isProcessing) return;
      this.isProcessing = true;
      this.savePuzzleResult({
        status: "skipped",
        answerPgn: null,
      });
      this.checkQuizComplete();
    },

    checkQuizComplete() {
      if (this.isReviewMode) {
        this.isProcessing = false;
        return;
      }

      if (this.nextPuzzleTimeoutId) {
        clearTimeout(this.nextPuzzleTimeoutId);
        this.nextPuzzleTimeoutId = null;
      }

      if (this.attemptedPuzzleCount === this.scenarios.length) {
        // All puzzles attempted, quiz is complete
        this.isReviewMode = true;
        this.showResultsModal();
        this.isProcessing = false;
        return;
      }

      const delayTimes = {
        correct: 1000,
        incorrect: 500,
        skipped: 300,
      };

      const delay = delayTimes[this.currentResult?.status] ?? 500;

      this.nextPuzzleTimeoutId = setTimeout(() => {
        this.nextPuzzle();
        this.isProcessing = false;
        this.nextPuzzleTimeoutId = null;
      }, delay);
    },

    nextPuzzle() {
      if (this.currentIndex < this.scenarios.length - 1) {
        this.currentIndex++;
      }
    },

    navigateToPuzzle(index) {
      if (!this.isReviewMode) return;

      if (index < this.puzzleResults.length) {
        this.currentIndex = index;
        this.isResultsModalActive = false;
      }
    },

    showResultsModal() {
      if (this.attemptedPuzzleCount === this.scenarios.length) {
        this.isReviewMode = true;
      }
      this.isResultsModalActive = true;
    },

    restartQuiz() {
      if (this.nextPuzzleTimeoutId) {
        clearTimeout(this.nextPuzzleTimeoutId);
        this.nextPuzzleTimeoutId = null;
      }
      this.resetPuzzleResults();
      this.isResultsModalActive = false;
      this.isReviewMode = false;
      this.isProcessing = false;

      if (this.currentIndex === 0) {
        this.currentIndex = -1; // to force loading in case the current index is 0
      }
      this.$nextTick(() => {
        this.currentIndex = 0;
      });
    },

    resetPuzzleResults() {
      this.puzzleResults = this.scenarios.map((scenario) => ({
        scenarioId: scenario.id,
        pgn: scenario.pgn,
        status: null,
        answerPgn: null,
      }));
    },

    exitQuiz() {
      this.$emit("exitQuiz");
    },
  },
};
</script>

<style scoped>
.quiz-manager {
  width: 100%;
  position: static; /* Change from relative to static to prevent containment */
}

.quiz-controls {
  margin: 3rem auto 0; /* top, horizontal, bottom */
  max-width: 820px;
  width: 100%;
  transition: all 0.3s ease;
}

.quiz-controls.presentation-mode {
  position: fixed; /* Change from absolute to fixed */
  right: 0;
  top: 50%;
  transform: rotate(-90deg) translate(50%, -100%);
  transform-origin: right top;
  margin: 0;
  padding: 1rem;
  width: auto;
  max-width: none;
  z-index: 1;
}

.quiz-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}
</style>
